import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTweaks } from "../hooks/useTweaks";
import { BackgroundPaths } from "../components/ui/BackgroundPaths";
import { SunIcon, MoonIcon, GithubIcon, SparkleIcon, LayersIcon } from "../components/ui/Icons";

// ── Static data ───────────────────────────────────────────────────────────────

const STATS = [
  { val: "8", suf: "+ logsources", lbl: "Windows · Linux · macOS · Cloud · Network" },
  { val: "3,000", suf: "+", lbl: "Sigma rules in the SigmaHQ repository" },
  { val: "350", suf: "+", lbl: "MITRE ATT&CK techniques covered" },
  { val: "100", suf: "%", lbl: "Open source · No account · No API key" },
];

const STEPS = [
  {
    n: "01", title: "Import",
    desc: "Paste a Sigma YAML rule, drop a .yml file, or upload a .zip of your entire rule library. Multi-document YAML files with --- separators are also supported.",
  },
  {
    n: "02", title: "Configure",
    desc: "Set rule IDs, filter by severity or status, map custom field names, and toggle output options like no_full_log, all from the Settings panel.",
  },
  {
    n: "03", title: "Export",
    desc: "Download a validated, schema-correct Wazuh XML file. Large rulesets are automatically split into multiple files at a configurable size limit.",
  },
];

const FEATS = [
  { icon: "⧉", title: "Intelligent Field Mapping",    desc: "Translates Sigma field names (Image, CommandLine, EventID, DestinationPort) to the correct Wazuh JSON paths for each logsource. Custom mappings are configurable per session in the Settings panel." },
  { icon: "◈", title: "MITRE ATT&CK Integration",     desc: "Parses attack.t* tags from Sigma rule metadata and generates native Wazuh <mitre><id> elements. Technique IDs and tactic groups are embedded automatically, no manual tagging required." },
  { icon: "↑", title: "Batch Processing",              desc: "Process multi-document YAML files with --- separators, single-rule files, or ZIP archives of entire rule directories. Large exports are automatically split into multiple XML files." },
  { icon: "≋", title: "PCRE2 Pattern Generation",      desc: "Converts contains, endswith, startswith, all of selection_* and re modifiers into PCRE2 patterns Wazuh executes natively. Case-insensitive matching is applied to all string comparisons automatically." },
  { icon: "◎", title: "Multi-Platform Support",        desc: "Covers Windows (Sysmon, Security, PowerShell), Linux, macOS, AWS CloudTrail, Azure, GCP, Office 365, and network logsources. Each maps to the correct Wazuh parent SID and field paths." },
  { icon: "✦", title: "Rule Validation",               desc: "Validates every output against the Wazuh XML schema. Conversion warnings (unknown fields, unsupported modifiers, empty conditions) surface before you download. Errors are listed per-rule in batch mode." },
];

const RESOURCES = [
  {
    icon: "⎘", title: "Sigma Rule Syntax",
    sub: "Official documentation on the full Sigma rule specification: conditions, modifiers, logsources, aggregations, and correlations.",
    href: "https://sigmahq.io/docs/basics/rules.html",
    domain: "sigmahq.io",
  },
  {
    icon: "◎", title: "MITRE ATT&CK Framework",
    sub: "The definitive knowledge base of adversary tactics and techniques. Essential context for writing and evaluating detection rules.",
    href: "https://attack.mitre.org/",
    domain: "attack.mitre.org",
  },
  {
    icon: "◉", title: "Wazuh Ruleset Docs",
    sub: "Official guide on writing, deploying, and testing custom Wazuh detection rules, decoders, and active response scripts.",
    href: "https://documentation.wazuh.com/current/user-manual/ruleset/ruleset-xml-syntax/rules.html",
    domain: "documentation.wazuh.com",
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function LandingNavbar({ isDark, onThemeToggle }: { isDark: boolean; onThemeToggle: () => void }) {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 768) setMenuOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <nav className={menuOpen ? "menu-open" : ""}>
      <Link to="/" className="logo">
        <span className="logo-word">Sig<em>Waz</em></span>
      </Link>
      <div className="nav-links">
        <a href="#hiw"       className="nav-link" onClick={() => setMenuOpen(false)}>How it works</a>
        <a href="#features"  className="nav-link" onClick={() => setMenuOpen(false)}>Features</a>
        <a href="#previews"  className="nav-link" onClick={() => setMenuOpen(false)}>Previews</a>
        <a href="#resources" className="nav-link" onClick={() => setMenuOpen(false)}>Resources</a>
      </div>

      <div className="nav-right">
        <span className="ver-chip">v1.0.0</span>
        <a href="https://github.com/heraclescap" target="_blank" rel="noreferrer" className="nav-icon-btn" aria-label="GitHub">
          <GithubIcon size={17} />
        </a>
        <button
          className="nav-icon-btn"
          onClick={onThemeToggle}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? <SunIcon /> : <MoonIcon />}
        </button>
        <Link to="/converter" className="btn-primary sm">Try SigWaz →</Link>
        <button
          className="nav-hamburger nav-icon-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? "✕" : "☰"}
        </button>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <div className="hero" id="top">
      <h1>
        Convert Sigma Rules<br />
        to Wazuh,{" "}<span className="hero-em-gradient">Instantly.</span>
      </h1>
      <p className="hero-sub">
        Transform any YAML Sigma detection rule into production-ready Wazuh XML.
        Single rules or entire rulesets. No scripts, no config, no friction.
      </p>
      <div className="hero-btns">
        <Link to="/converter" className="btn-primary">Start Converting →</Link>
        <a href="https://github.com/SigmaHQ/sigma" target="_blank" rel="noreferrer" className="btn-secondary">
          Browse Sigma Rules ↗
        </a>
      </div>
    </div>
  );
}

function StatsStrip() {
  return (
    <div className="stats-strip">
      <div className="stats-inner">
        {STATS.map((s, i) => (
          <div key={i} className="stat-item">
            <div className="stat-val">{s.val}<span>{s.suf}</span></div>
            <div className="stat-lbl">{s.lbl}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HowItWorks() {
  return (
    <section className="section" id="hiw" aria-label="How it works">
      <div className="hiw-header">
        <div className="sec-eyebrow">How it works</div>
        <h2 className="sec-title">From Sigma to Wazuh in three steps</h2>
        <p className="sec-sub">No setup. No API key. Everything runs locally.</p>
      </div>
      <div className="hiw-steps">
        <div className="hiw-conn hiw-conn-1" />
        <div className="hiw-conn hiw-conn-2" />
        {STEPS.map((s, i) => (
          <div key={i} className="hiw-step">
            <div className="hiw-num">{s.n}</div>
            <h3>{s.title}</h3>
            <p>{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="section" id="features" aria-label="Features">
      <div className="sec-eyebrow">Features</div>
      <h2 className="sec-title">Everything detection engineers need</h2>
      <p className="sec-sub">Built to produce reliable, production-quality Wazuh output every time.</p>
      <div className="feat-grid">
        {FEATS.map((f, i) => (
          <div key={i} className="card feat-card">
            <div className="feat-icon">{f.icon}</div>
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function CLISection() {
  return (
    <div className="cli-section">
      <div className="cli-card card">
        <div className="cli-pane-term">
          <div className="cli-winbar">
            <div className="cli-wdots">
              <div className="cli-wd r" /><div className="cli-wd y" /><div className="cli-wd g" />
            </div>
            <span className="cli-wtitle">Terminal: sigwaz cli</span>
          </div>
          <div className="cli-code">
            <span className="cli-ps">$</span>{" sigwaz batch windows/proc_creation/ \\\n"}
            {"    -o output/ "}
            <span className="cli-inf">{"--split 50 --min-level medium\n"}</span>
            <span className="cli-ps2">{"╭─ SigWaz  v1.0.0 ──────────────────────────╮\n"}</span>
            <span className="cli-ps2">{"│  Sigma → Wazuh  ·  State-of-the-art       │\n"}</span>
            <span className="cli-ps2">{"╰────────────────────────────────────────────╯\n"}</span>
            {"\n"}
            {"  Found "}<span className="cli-hi">312</span>{" Sigma rule(s), converting…\n"}
            {"  Filters: "}<span className="cli-inf">min-level=medium\n</span>
            {"\n"}
            <span className="cli-ok">✓ 268</span>{" converted  "}
            <span className="cli-inf">⚠ 37</span>{" skipped  "}
            {"✕ 7 errors  "}
            <span className="cli-ok">271</span>{" Wazuh rules\n\n"}
            <span className="cli-ok">↓</span>{" "}<span className="cli-hi">proc_creation_rules-1.xml</span>{"  (50 rules · 41.2 KB)\n"}
            <span className="cli-ok">↓</span>{" "}<span className="cli-hi">proc_creation_rules-2.xml</span>{"  (50 rules · 40.8 KB)\n"}
            <span className="cli-ok">↓</span>{" "}<span className="cli-hi">proc_creation_rules-3.xml</span>{"  (50 rules · 39.7 KB)\n"}
            <span className="cli-ok">↓</span>{" "}<span className="cli-hi">proc_creation_rules-4.xml</span>{"  (50 rules · 41.0 KB)\n"}
            <span className="cli-ok">↓</span>{" "}<span className="cli-hi">proc_creation_rules-5.xml</span>{"  (50 rules · 40.3 KB)\n"}
            <span className="cli-ok">↓</span>{" "}<span className="cli-hi">proc_creation_rules-6.xml</span>{"  (21 rules · 17.4 KB)\n\n"}
            <span className="cli-ok">✓</span>{" XML validation passed  (271 rules · 0 warnings)\n"}
            <span className="cli-ps2">$</span>{" ▌"}
          </div>
        </div>
        <div className="cli-pane-text">
          <div className="cli-eyebrow">Also available as a CLI</div>
          <h3>SigWaz in your pipeline</h3>
          <p>
            The same conversion engine as a command-line tool. Batch-convert entire Sigma rule directories,
            filter by level or status with <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--accent)" }}>--min-level</code> and{" "}
            <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--accent)" }}>--excluded-statuses</code>,
            and integrate seamlessly into CI/CD pipelines.
          </p>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <a href="https://github.com/heraclescap/sigwaz-cli" target="_blank" rel="noreferrer" className="btn-primary sm">
              View on GitHub ↗
            </a>
            <span className="cli-install"><span className="cli-ps">$</span> pip install "git+https://github.com/heraclescap/sigwaz.git#subdirectory=backend"</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConverterMockup() {
  return (
    <div className="mockup-section">
      <div className="mockup-window card">
        <div className="mw-chrome">
          <div className="mw-dots">
            <div className="mw-dot mw-dot-r" /><div className="mw-dot mw-dot-y" /><div className="mw-dot mw-dot-g" />
          </div>
          <div className="mw-url"><span className="url-dot" />sigwaz / converter</div>
        </div>
        {/* Sub-header: mode tabs + convert button */}
        <div className="mw-subhdr">
          <div className="mw-mode-tabs">
            <div className="mw-mode-tab active"><SparkleIcon size={10} /> Rules Editor</div>
            <div className="mw-mode-tab"><LayersIcon size={10} /> Batch Import</div>
          </div>
          <div className="mw-subhdr-right">
            <span className="mw-settings-btn">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54C14.46 2.18 14.25 2 14 2h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
              </svg>
              Settings
            </span>
            <span className="mw-convert-btn">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
              Convert
            </span>
          </div>
        </div>
        <div className="mw-panels">
          {/* YAML panel */}
          <div className="mw-panel">
            <div className="mw-phdr">
              <span className="lang-tag yaml">YAML · Sigma</span>
              <span className="lang-file">proc_creation_win_ps_download_cradle.yml</span>
            </div>
            <div className="mw-code">
              <span className="cc"># Sigma Detection Rule</span>{"\n"}
              <span className="ck">title</span><span className="cp">: </span><span className="cs">Suspicious PowerShell Cradle</span>{"\n"}
              <span className="ck">status</span><span className="cp">: </span><span className="cb">experimental</span>{"\n"}
              <span className="ck">level</span><span className="cp">: </span><span className="cb">high</span>{"\n"}
              <span className="ck">logsource</span><span className="cp">:</span>{"\n"}
              {"    "}<span className="ck">product</span><span className="cp">: </span><span className="cs">windows</span>{"\n"}
              {"    "}<span className="ck">category</span><span className="cp">: </span><span className="cs">process_creation</span>{"\n"}
              <span className="ck">tags</span><span className="cp">:</span>{"\n"}
              {"  "}<span className="cp">- </span><span className="cs">attack.t1059.001</span>{"\n"}
              <span className="ck">detection</span><span className="cp">:</span>{"\n"}
              {"  "}<span className="ck">selection</span><span className="cp">:</span>{"\n"}
              {"    "}<span className="ck">Image|endswith</span><span className="cp">:</span>{"\n"}
              {"      "}<span className="cp">- </span><span className="cs">{"'\\\\powershell.exe'"}</span>{"\n"}
              {"    "}<span className="ck">CommandLine|contains</span><span className="cp">:</span>{"\n"}
              {"      "}<span className="cp">- </span><span className="cs">{"'DownloadString'"}</span>{"\n"}
              <span className="ck">condition</span><span className="cp">: </span><span className="cs">selection</span>
            </div>
          </div>
          {/* XML panel */}
          <div className="mw-panel">
            <div className="mw-phdr">
              <span className="lang-tag xml">XML · Wazuh</span>
              <span className="lang-file">proc_creation_win_ps_download_cradle_rules.xml</span>
            </div>
            <div className="mw-code">
              <span className="cc">{"<!-- SigWaz v1.0.0 | 1 rule -->"}</span>{"\n\n"}
              <span className="cp">{"<"}</span><span className="ct">group</span>{" "}<span className="ca">name</span><span className="cp">{"=\""}</span><span className="cv">sigma_windows,</span><span className="cp">{"\">"}</span>{"\n\n"}
              {"  "}<span className="cp">{"<"}</span><span className="ct">rule</span>{" "}<span className="ca">id</span><span className="cp">{"=\""}</span><span className="cv">900001</span><span className="cp">{"\""}</span>{" "}<span className="ca">level</span><span className="cp">{"=\""}</span><span className="cv">12</span><span className="cp">{"\">"}</span>{"\n"}
              {"    "}<span className="cp">{"<"}</span><span className="ct">if_sid</span><span className="cp">{">"}</span><span className="cx">61603</span><span className="cp">{"</if_sid>"}</span>{"\n"}
              {"    "}<span className="cp">{"<"}</span><span className="ct">field</span>{" "}<span className="ca">name</span><span className="cp">{"=\""}</span><span className="cv">win.eventdata.image</span><span className="cp">{"\""}</span>{" "}<span className="ca">type</span><span className="cp">{"=\""}</span><span className="cv">pcre2</span><span className="cp">{">"}</span><span className="cs">{"(?i)(?:\\powershell\\.exe)$"}</span><span className="cp">{"</field>"}</span>{"\n"}
              {"    "}<span className="cp">{"<"}</span><span className="ct">field</span>{" "}<span className="ca">name</span><span className="cp">{"=\""}</span><span className="cv">win.eventdata.commandLine</span><span className="cp">{"\""}</span>{" "}<span className="ca">type</span><span className="cp">{"=\""}</span><span className="cv">pcre2</span><span className="cp">{">"}</span><span className="cs">{"(?i)DownloadString"}</span><span className="cp">{"</field>"}</span>{"\n"}
              {"    "}<span className="cp">{"<"}</span><span className="ct">description</span><span className="cp">{">"}</span><span className="cx">Suspicious PowerShell Cradle</span><span className="cp">{"</description>"}</span>{"\n"}
              {"    "}<span className="cp">{"<"}</span><span className="ct">group</span><span className="cp">{">"}</span><span className="cx">windows,process_creation,</span><span className="cp">{"</group>"}</span>{"\n"}
              {"    "}<span className="cp">{"<"}</span><span className="ct">mitre</span><span className="cp">{">"}</span><span className="cp">{"<"}</span><span className="ct">id</span><span className="cp">{">"}</span><span className="cx">T1059.001</span><span className="cp">{"</id></mitre>"}</span>{"\n"}
              {"  "}<span className="cp">{"</"}</span><span className="ct">rule</span><span className="cp">{">"}</span>{"\n\n"}
              <span className="cp">{"</"}</span><span className="ct">group</span><span className="cp">{">"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Resources() {
  return (
    <section className="section" id="resources" aria-label="Resources">
      <div className="sec-eyebrow">Resources</div>
      <h2 className="sec-title">Learn the ecosystem</h2>
      <p className="sec-sub">
        Everything you need to write, convert, and ship detection rules, from the Sigma and Wazuh communities.
      </p>
      <div className="res-grid">
        {RESOURCES.map((r, i) => (
          <a key={i} href={r.href} target="_blank" rel="noreferrer" className="card res-card">
            <div className="res-icon">{r.icon}</div>
            <div className="res-title">{r.title}</div>
            <p className="res-sub">{r.sub}</p>
            <div className="res-link">
              <span className="res-domain">{r.domain}</span>
              <span className="res-arr">↗</span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <div className="cta-outer">
      <div className="cta-inner card">
        <div className="cta-body">
          <h2>Ready to ship detection rules?</h2>
          <p>Paste a Sigma rule and get production-ready Wazuh XML in under a second. No signup. No limits.</p>
          <div className="cta-btns">
            <Link to="/converter" className="btn-primary">Start Converting →</Link>
            <a href="https://github.com/SigmaHQ/sigma" target="_blank" rel="noreferrer" className="btn-secondary">
              Browse 3,000+ Sigma Rules ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function PageFooter() {
  return (
    <footer>
      <div className="footer-inner">
        <Link to="/" className="logo">
          <span className="logo-word">Sig<em>Waz</em></span>
        </Link>
        <div className="footer-links">
          <a href="https://sigmahq.io/docs/basics/rules.html" target="_blank" rel="noreferrer" className="footer-link ext">Sigma Docs</a>
          <a href="https://github.com/SigmaHQ/sigma" target="_blank" rel="noreferrer" className="footer-link ext">Sigma GitHub</a>
          <a href="https://documentation.wazuh.com/current/user-manual/ruleset/ruleset-xml-syntax/rules.html" target="_blank" rel="noreferrer" className="footer-link ext">Wazuh Docs</a>
          <a href="https://attack.mitre.org/" target="_blank" rel="noreferrer" className="footer-link ext">MITRE ATT&CK</a>
        </div>
        <div className="footer-copy">© 2026 SigWaz · Open source · MIT License</div>
      </div>
    </footer>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const { tweaks, setTweak } = useTweaks();

  useEffect(() => {
    document.documentElement.dataset.page = "landing";
    document.title = "SigWaz";
    const desc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (desc) desc.content = "Free online tool to convert Sigma detection rules to Wazuh XML. Batch YAML & ZIP conversion, MITRE ATT&CK mapping, PCRE2 pattern generation. No account, no setup.";
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = "https://sigwaz.com/";
    return () => { delete document.documentElement.dataset.page; };
  }, []);

  return (
    <div className="land-page">
      <BackgroundPaths />
      {tweaks.aurora  && <div className="bg-layer bg-aurora" />}
      {tweaks.dotGrid && <div className="bg-layer bg-dots" />}
      <div className="land-app">
        <LandingNavbar
          isDark={!tweaks.lightMode}
          onThemeToggle={() => setTweak("lightMode", !tweaks.lightMode)}
        />
        <main>
          <Hero />
          <StatsStrip />
          <HowItWorks />
          <Features />
          <div id="previews">
            <section className="section" aria-label="Previews">
              <div className="sec-eyebrow">Previews</div>
              <h2 className="sec-title">SigWaz in action</h2>
              <p className="sec-sub">The same conversion engine, two interfaces. Choose what fits your workflow.</p>
            </section>

            <div className="preview-lead">
              <span className="preview-tag">01 · CLI</span>
              <h3 className="preview-lead-title">Automate at scale: batch convert in your pipeline</h3>
              <p className="preview-lead-desc">Process entire Sigma rule directories with a single command. Filter by severity or status, split output automatically, and plug directly into CI/CD, no browser required.</p>
            </div>
            <CLISection />

            <div className="preview-lead">
              <span className="preview-tag">02 · Web App</span>
              <h3 className="preview-lead-title">Zero setup: convert in your browser, instantly</h3>
              <p className="preview-lead-desc">Paste a rule or drop a file and get schema-valid Wazuh XML in under a second. Works fully client-side: no account, no install, no API key ever leaves your browser.</p>
            </div>
            <ConverterMockup />
          </div>
          <Resources />
          <CTA />
        </main>
        <PageFooter />
      </div>
    </div>
  );
}
